import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { 
  Users, 
  FileText, 
  Receipt, 
  FolderOpen, 
  UserPlus,
  FileTextIcon,
  PlusCircle,
  Building,
  Settings,
  Calendar,
  Activity
} from "lucide-react";

interface CommandPaletteProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function CommandPalette({ open, setOpen }: CommandPaletteProps) {
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, setOpen]);

  const runCommand = (command: () => void) => {
    setOpen(false);
    command();
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        <CommandGroup heading="Quick Actions">
          <CommandItem
            onSelect={() => runCommand(() => navigate("/clients"))}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            <span>Add New Client</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/proposals"))}
            className="flex items-center gap-2"
          >
            <FileTextIcon className="h-4 w-4" />
            <span>Create Proposal</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/invoices"))}
            className="flex items-center gap-2"
          >
            <Receipt className="h-4 w-4" />
            <span>Create Invoice</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/projects"))}
            className="flex items-center gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            <span>Add Project</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/people"))}
            className="flex items-center gap-2"
          >
            <Users className="h-4 w-4" />
            <span>Add Person</span>
          </CommandItem>
        </CommandGroup>

        <CommandGroup heading="Navigation">
          <CommandItem
            onSelect={() => runCommand(() => navigate("/"))}
            className="flex items-center gap-2"
          >
            <Activity className="h-4 w-4" />
            <span>Dashboard</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/clients"))}
            className="flex items-center gap-2"
          >
            <Building className="h-4 w-4" />
            <span>Clients</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/proposals"))}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            <span>Proposals</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/invoices"))}
            className="flex items-center gap-2"
          >
            <Receipt className="h-4 w-4" />
            <span>Invoices</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/projects"))}
            className="flex items-center gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            <span>Projects</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/people"))}
            className="flex items-center gap-2"
          >
            <Users className="h-4 w-4" />
            <span>People</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/activities"))}
            className="flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            <span>Activities</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/settings"))}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}